import React from 'react';
import { Calendar, Clock, Users, ArrowLeft, Linkedin } from 'lucide-react';
import { useGetAllUpcomingEvents } from '../services';
import { Loading, Error } from '../shared/components';

const UpcomingEventsPage: React.FC = () => {
  const { upcomingEvents, isLoading, error } = useGetAllUpcomingEvents();

  // Loading state
  if (isLoading) {
    return <Loading message="Loading upcoming events..." />;
  }

  // Error state
  if (error) {
    return (
      <Error
        message={error}
        showBackButton={true}
        backButtonText="Back to Events"
        onBackClick={() => (window.location.href = '/events')}
      />
    );
  }

  return (
    <div className="py-6 sm:py-12">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <button
              onClick={() => (window.location.href = '/events')}
              className="inline-flex items-center text-primary-500 hover:opacity-80 transition-opacity duration-200 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Events
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 text-primary-500">
              All Upcoming Events
            </h1>
            <div className="w-12 sm:w-16 h-0.5 rounded bg-primary-500"></div>
          </div>

          {/* Events Count */}
          {upcomingEvents.length > 0 && (
            <div className="mb-6">
              <p className="text-lg text-secondary-700">
                Showing {upcomingEvents.length} upcoming event
                {upcomingEvents.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}

          {/* Upcoming Events Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {upcomingEvents.map((event) => (
              <div
                key={event.id}
                className="bg-gray-100 p-4 sm:p-6 rounded-lg border-l-4 border-l-primary-500 cursor-pointer transition-all duration-200 hover:shadow-lg hover:bg-gray-50"
                onClick={() =>
                  event.webinarLink && window.open(event.webinarLink, '_blank')
                }
              >
                <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-secondary-700">
                  {event.title}
                </h3>

                <div className="space-y-2 sm:space-y-3">
                  {(event.dateStr || event.day) && (
                    <div className="flex items-start">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                      <span className="text-xs sm:text-sm text-secondary-700">
                        {event.dateStr}
                        {event.day && ` (${event.day})`}
                      </span>
                    </div>
                  )}
                  {(event.startTimeStr || event.endTimeStr) && (
                    <div className="flex items-start">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                      <span className="text-xs sm:text-sm text-secondary-700">
                        {event.startTimeStr}
                        {event.endTimeStr && ` - ${event.endTimeStr}`}
                      </span>
                    </div>
                  )}
                  {event.speaker && (
                    <div className="flex items-start">
                      <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-2 mt-0.5 flex-shrink-0 text-secondary-700" />
                      <div className="flex-1">
                        <span className="text-xs sm:text-sm text-secondary-700">
                          Speaker: {event.speaker}
                        </span>
                        {event.linkedIn && (
                          <a
                            href={event.linkedIn}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center ml-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Linkedin className="w-3 h-3 sm:w-4 sm:h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {event.desc && (
                  <p className="text-xs sm:text-sm mt-3 text-secondary-700 line-clamp-3">
                    {event.desc}
                  </p>
                )}

                <div className="flex items-center justify-between mt-4">
                  {event.webinarLink ? (
                    <button className="text-xs underline hover:opacity-80 text-primary-500 hover:text-primary-600 transition-colors duration-200">
                      Register/Join Event
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">
                      Registration details coming soon
                    </span>
                  )}
                </div>
              </div>
            ))}

            {/* No Events State */}
            {upcomingEvents.length === 0 && (
              <div className="bg-gray-100 p-8 rounded-lg col-span-full text-center">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-primary-500 opacity-50" />
                <h3 className="text-xl font-semibold mb-2 text-primary-500">
                  No Upcoming Events
                </h3>
                <p className="text-secondary-700 mb-6">
                  We're planning exciting events for the future. Check back
                  soon!
                </p>
                <button
                  onClick={() => (window.location.href = '/events')}
                  className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 hover:shadow-lg transform hover:scale-105 transition-all duration-200 font-semibold"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Events
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpcomingEventsPage;
